// src/pages/RiskPage.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { fetchHistoricalPrices } from '../lib/api';
import {
  calcDailyReturns, calcVaR, calcCVaR, calcAnnualizedVol, calcSharpe,
  calcMaxDrawdown, calcBeta, calcDollarBeta, calcPositionDollarBetas,
  calcCorrelationMatrix, runFamaFrenchRegression, getApproximateFFFactors,
  calcExposures, checkConcentrationAlerts, mean
} from '../lib/riskEngine';
import { SleevePicker, ApiConfirmModal, RateLimitBanner, fmtMoney, fmtPct, fmtNum, colorClass, LoadingOverlay } from '../components';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, ReferenceLine, Cell } from 'recharts';

const COLORS = ['#58a6ff','#3fb950','#f85149','#d29922','#a371f7','#ffa657','#79c0ff','#56d364'];

// Stat card helper
function Stat({ label, value, sub, cls = '', wide = false }) {
  return (
    <div className="stat-card" style={wide ? { gridColumn: 'span 2' } : {}}>
      <div className="stat-label">{label}</div>
      <div className={`stat-value ${cls}`}>{value ?? '—'}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export default function RiskPage() {
  const { canEdit } = useAuth();
  const [sleeves, setSleeves]       = useState([]);
  const [positions, setPositions]   = useState([]);
  const [trades, setTrades]         = useState([]);   // position_trades for realized P&L
  const [activeSleeve, setActiveSleeve] = useState(null);
  const [risk, setRisk]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [calcProgress, setCalcProgress] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeTab, setActiveTab]   = useState('metrics');
  const [pnlDays, setPnlDays]       = useState(30);   // P&L window selector
  const [cachedHistorical, setCachedHistorical] = useState(null);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (positions.length > 0) runRisk(); }, [positions, activeSleeve, trades, pnlDays]);

  async function loadData() {
    setLoading(true);
    const [{ data: s }, { data: p }, { data: t }] = await Promise.all([
      supabase.from('sleeves').select('*').eq('is_active', true).order('name'),
      supabase.from('positions').select('*, securities(*)').eq('is_active', true),
      supabase.from('position_trades').select('*').eq('trade_type', 'exit').order('traded_at', { ascending: false }),
    ]);
    setSleeves(s || []);
    setPositions(p || []);
    setTrades(t || []);

    // Load last cached snapshot for sleeve (or fund-wide if no sleeve selected)
    try {
      let q = supabase.from('risk_snapshots').select('*').order('created_at', { ascending: false }).limit(1);
      if (activeSleeve) q = q.eq('sleeve_id', activeSleeve);
      else q = q.is('sleeve_id', null);
      const { data: snap } = await q;
      const last = snap?.[0];
      if (last) {
        // Normalize snapshot to match historicalRisk shape used by the UI
        const cachedBeta = last.beta_to_spy ?? null;
        setCachedHistorical({
          // Match historicalRisk field names exactly
          beta: cachedBeta,
          dollarBeta: null, // computed below in runRisk from beta * totalNav
          var95: last.portfolio_var_95 ?? null,
          var99: last.portfolio_var_99 ?? null,
          cvar95: last.portfolio_cvar_95 ?? null,
          vol: last.portfolio_volatility_ann ?? null,
          sharpe: last.sharpe_ratio ?? null,
          maxDD: last.max_drawdown ?? null,
          annReturn: null,
          portPrices: null,  // no chart data in snapshot
          posDollarBetas: null,
          ff: last.ff_market_beta != null ? {
            market_beta: last.ff_market_beta,
            mom: last.ff_mom ?? null,
            r_squared: last.ff_r_squared ?? null,
            observations: null,
            source: 'cached',
          } : null,
          correlation: last.geographic_weights?.correlation || null,
          computedAt: last.created_at,
          _from_snapshot: true,
        });
      } else {
        setCachedHistorical(null);
      }
    } catch {
      setCachedHistorical(null);
    }
    setLoading(false);
  }

  function runRisk() {
    const filtered = activeSleeve ? positions.filter(p => p.sleeve_id === activeSleeve) : positions;
    if (!filtered.length) { setRisk(null); return; }

    const totalNav = filtered.reduce((s, p) => s + Math.abs(p.market_value || (p.entry_price * p.quantity)), 0);
    const exposures = calcExposures(
      filtered.map(p => ({ ...p, sector: p.securities?.sector, asset_class: p.securities?.asset_class })),
      totalNav
    );
    const alerts = checkConcentrationAlerts(
      filtered.map(p => ({ ...p, ticker: p.securities?.ticker })),
      totalNav
    );

    // Total P&L — unrealized (open positions) + realized (closed trades in window)
    const unrealizedPnl = filtered.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);
    const totalCostBasis = filtered.reduce((s, p) => s + Math.abs((p.quantity || 0) * (p.entry_price || 0)), 0);

    // Realized P&L in selected window from position_trades
    const windowMs = pnlDays * 24 * 60 * 60 * 1000;
    const windowCutoff = new Date(Date.now() - windowMs);
    const filteredTrades = activeSleeve
      ? trades.filter(t => t.sleeve_id === activeSleeve)
      : trades;
    const recentTrades = filteredTrades.filter(t => new Date(t.traded_at) >= windowCutoff);
    const realizedPnl = recentTrades.reduce((s, t) => s + (t.realized_pnl || 0), 0);
    const totalPnl = unrealizedPnl + realizedPnl;
    const totalPnlPct = totalCostBasis > 0 ? (totalPnl / totalCostBasis) * 100 : 0;

    // Delta-adjusted exposure
    const totalDeltaAdj = filtered.reduce((s, p) =>
      s + (p.delta_adjusted_exposure || (p.direction === 'long' ? (p.market_value || 0) : -(p.market_value || 0))), 0);

    // Use live historicalRisk if available, otherwise cached snapshot
    const prevHistorical = risk?.historicalRisk || cachedHistorical || null;
    // Compute dollarBeta live from beta * totalNav (cache stores beta but not dollarBeta)
    let enrichedHistorical = prevHistorical;
    if (prevHistorical && prevHistorical.beta != null && !prevHistorical.dollarBeta) {
      enrichedHistorical = {
        ...prevHistorical,
        dollarBeta: prevHistorical.beta * totalNav,
        posDollarBetas: calcPositionDollarBetas(filtered, prevHistorical.beta, totalNav),
      };
    }

    setRisk({
      exposures, alerts, totalDeltaAdj, totalNav, positions: filtered,
      unrealizedPnl, realizedPnl, totalPnl, totalPnlPct, totalCostBasis,
      recentTradeCount: recentTrades.length,
      historicalRisk: enrichedHistorical,
    });
  }

  async function handleCalcHistorical() {
    setShowConfirm(false);
    setCalculating(true);
    setCalcProgress('Fetching factor data...');

    const filtered = activeSleeve ? positions.filter(p => p.sleeve_id === activeSleeve) : positions;
    const totalNav = filtered.reduce((s, p) => s + Math.abs(p.market_value || (p.entry_price * p.quantity)), 0);

    // 1. Fetch real Fama-French factors via edge function
    let ffFactorData = null;
    let ffSource = 'estimated';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/security-lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ action: 'ff_factors', months: 14 }),
      });
      const json = await res.json();
      if (json.factors && json.factors.length > 30) {
        ffFactorData = json.factors;
        ffSource = json.source || 'ken_french';
        setCalcProgress(`Got ${json.factors.length} days of real FF factors. Fetching prices...`);
      } else {
        setCalcProgress('FF factor fetch failed, using estimates. Fetching prices...');
      }
    } catch { setCalcProgress('FF factor fetch failed, using estimates. Fetching prices...'); }

    // 2. Fetch historical prices for all positions + SPY
    const tickers = [...new Set([
      ...filtered.map(p => p.securities?.ticker).filter(t => t && t !== 'CASH'),
      'SPY'
    ])];
    const historicalMap = {};
    for (let i = 0; i < tickers.length; i++) {
      const ticker = tickers[i];
      setCalcProgress(`Fetching ${ticker} (${i + 1}/${tickers.length})...`);
      const prices = await fetchHistoricalPrices(ticker, 13);
      if (prices.length > 10) historicalMap[ticker] = prices.map(p => p.adjClose || p.close);
    }

    // 3. Build returns map
    const returnsMap = {};
    for (const [t, prices] of Object.entries(historicalMap)) {
      returnsMap[t] = calcDailyReturns(prices);
    }

    // 4. Weights — only tickers with data, normalized
    const rawWeights = {};
    for (const p of filtered) {
      const t = p.securities?.ticker;
      if (!t || t === 'CASH' || t === 'SPY') continue;
      if (!returnsMap[t] || returnsMap[t].length < 30) continue;
      rawWeights[t] = (rawWeights[t] || 0) + Math.abs(p.market_value || p.entry_price * p.quantity || 0);
    }
    const weightSum = Object.values(rawWeights).reduce((s, w) => s + w, 0);
    const weights = weightSum > 0
      ? Object.fromEntries(Object.entries(rawWeights).map(([t, w]) => [t, w / weightSum]))
      : Object.fromEntries(Object.keys(rawWeights).map(t => [t, 1 / Object.keys(rawWeights).length]));

    // 5. Portfolio return series — align tickers from the end
    const weightedTickers = Object.keys(weights);
    const commonLen = weightedTickers.length > 0
      ? Math.min(...weightedTickers.map(t => returnsMap[t]?.length || 0))
      : 0;

    const portReturns = [];
    if (commonLen >= 30) {
      for (let i = 0; i < commonLen; i++) {
        let r = 0;
        for (const t of weightedTickers) {
          const arr = returnsMap[t];
          r += weights[t] * arr[arr.length - commonLen + i];
        }
        portReturns.push(r);
      }
    }

    // 6. Align SPY to same trailing window
    const rawSpy = returnsMap['SPY'] || [];
    const alignLen = Math.min(portReturns.length, rawSpy.length);
    const spyReturns = rawSpy.slice(-alignLen);
    const portAligned = portReturns.slice(-alignLen);

    // 7. Align FF factors to portAligned length — use last N rows
    let ffFactors;
    if (ffFactorData && ffFactorData.length >= alignLen) {
      ffFactors = ffFactorData.slice(-alignLen).map(f => ({ ...f, _source: 'ken_french' }));
    } else {
      ffFactors = getApproximateFFFactors(alignLen);
      ffSource = 'estimated';
    }

    setCalcProgress('Computing statistics...');

    // 8. Compute metrics
    const beta = spyReturns.length > 20 ? calcBeta(portAligned, spyReturns) : null;
    const dollarBeta = calcDollarBeta(beta, totalNav);
    const posDollarBetas = calcPositionDollarBetas(filtered, beta, totalNav);

    const ffLen = Math.min(portAligned.length, 252);
    const portSlice = portAligned.slice(-ffLen);
    const ffSlice = ffFactors.slice(-ffLen);
    const ffResult = ffLen >= 30 ? runFamaFrenchRegression(portSlice, ffSlice) : null;
    if (ffResult) ffResult.source = ffSource;

    const portPrices = portReturns.reduce((acc, r) => {
      acc.push(acc[acc.length - 1] * (1 + r));
      return acc;
    }, [100]);

    const historicalRisk = {
      var95: portReturns.length > 0 ? calcVaR(portReturns, 0.95, totalNav) : null,
      var99: portReturns.length > 0 ? calcVaR(portReturns, 0.99, totalNav) : null,
      cvar95: portReturns.length > 0 ? calcCVaR(portReturns, 0.95, totalNav) : null,
      vol: portReturns.length > 0 ? calcAnnualizedVol(portReturns) * 100 : null,     // annualized
      sharpe: calcSharpe(portReturns),
      maxDD: portPrices.length > 1 ? calcMaxDrawdown(portPrices) * 100 : null,
      beta,
      dollarBeta,
      posDollarBetas,
      annReturn: portReturns.length > 0 ? mean(portReturns) * 252 * 100 : null,
      correlation: Object.keys(returnsMap).length > 1 ? calcCorrelationMatrix(returnsMap) : null,
      ff: ffResult,
      portReturns, portPrices, spyReturns: rawSpy, tickers,
      computedAt: new Date().toISOString(),
    };

    setRisk(r => ({ ...r, historicalRisk }));

    // Persist snapshot for cross-tab / cross-user sync
    try {
      const exposuresNow = calcExposures(
        filtered.map(p => ({ ...p, sector: p.securities?.sector, asset_class: p.securities?.asset_class })),
        totalNav
      );
      await supabase.from('risk_snapshots').insert({
        sleeve_id: activeSleeve || null,
        snapshot_date: new Date().toISOString().slice(0, 10),
        gross_exposure: exposuresNow.grossExposure,
        net_exposure: exposuresNow.netExposure,
        long_exposure: exposuresNow.longExposure,
        short_exposure: exposuresNow.shortExposure,
        net_exposure_pct: exposuresNow.netPct,
        gross_exposure_pct: exposuresNow.grossPct,
        portfolio_var_95: historicalRisk.var95,
        portfolio_var_99: historicalRisk.var99,
        portfolio_cvar_95: historicalRisk.cvar95,
        portfolio_volatility_ann: historicalRisk.vol,
        sharpe_ratio: historicalRisk.sharpe,
        max_drawdown: historicalRisk.maxDD,
        beta_to_spy: historicalRisk.beta,
        ff_market_beta: historicalRisk.ff?.market_beta,
        ff_smb: historicalRisk.ff?.smb,
        ff_hml: historicalRisk.ff?.hml,
        ff_mom: historicalRisk.ff?.mom,
        ff_r_squared: historicalRisk.ff?.r_squared,
        sector_weights: exposuresNow.sectorWeights,
        asset_class_weights: exposuresNow.assetClassWeights,
        geographic_weights: { correlation: historicalRisk.correlation },
        top_long_positions: null,
        top_short_positions: null,
        concentration_hhi: null,
      });
      // Cached shape matches historicalRisk exactly for UI re-use
      setCachedHistorical({ ...historicalRisk, _from_snapshot: false });
    } catch {
      // ignore cache write failures
    }
    setCalculating(false);
    setCalcProgress('');
  }

  if (loading) return <LoadingOverlay message="Loading..." />;

  const filtered = activeSleeve ? positions.filter(p => p.sleeve_id === activeSleeve) : positions;
  const h = risk?.historicalRisk;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Risk & Analytics</div>
          <div className="page-subtitle">Portfolio risk metrics, factor exposures & concentration analysis</div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={loadData} disabled={loading}>↻ Refresh</button>
        {canEdit && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowConfirm(true)}
            disabled={calculating || !filtered.length}
          >
            {calculating ? `↺ ${calcProgress || 'Pulling...'}` : '↺ Pull Data'}
          </button>
        )}
      </div>

      <div className="page-body section-gap">
        {canEdit && <RateLimitBanner provider="yahoo_finance" />}

        <SleevePicker sleeves={sleeves} activeId={activeSleeve} onChange={setActiveSleeve} />

        {risk?.alerts?.map((a, i) => (
          <div key={i} className={`alert alert-${a.severity}`}>
            <span>⚠</span><span>{a.message}</span>
          </div>
        ))}

        {!filtered.length ? (
          <div className="card"><div className="empty-state"><div className="empty-state-title">No positions to analyze</div></div></div>
        ) : (
          <>
            {/* Tabs */}
            <div className="tabs">
              {['metrics', 'exposure', 'greeks', 'factors', 'correlation'].map(t => (
                <button key={t} className={`tab ${activeTab === t ? 'active' : ''}`} onClick={() => setActiveTab(t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>

            {/* ── METRICS TAB ── */}
            {activeTab === 'metrics' && (
              <div className="section-gap">

                {/* P&L section — always shown, includes realized+unrealized */}
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">Portfolio P&L</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>Realized window:</span>
                      {[7, 30, 90, 365].map(d => (
                        <button
                          key={d}
                          className={`tab ${pnlDays === d ? 'active' : ''}`}
                          style={{ padding: '3px 10px', fontSize: 11 }}
                          onClick={() => setPnlDays(d)}
                        >{d}d</button>
                      ))}
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="stat-grid stat-grid-4">
                      <Stat
                        label="Total P&L"
                        value={fmtMoney(risk?.totalPnl)}
                        sub={risk?.totalPnlPct != null ? fmtPct(risk.totalPnlPct) + ' on cost' : undefined}
                        cls={colorClass(risk?.totalPnl)}
                      />
                      <Stat
                        label="Unrealized P&L"
                        value={fmtMoney(risk?.unrealizedPnl)}
                        sub={`${filtered.length} open positions`}
                        cls={colorClass(risk?.unrealizedPnl)}
                      />
                      <Stat
                        label={`Realized P&L (${pnlDays}d)`}
                        value={fmtMoney(risk?.realizedPnl)}
                        sub={`${risk?.recentTradeCount || 0} closed trade${risk?.recentTradeCount !== 1 ? 's' : ''}`}
                        cls={colorClass(risk?.realizedPnl)}
                      />
                      <Stat
                        label="Cost Basis"
                        value={fmtMoney(risk?.totalCostBasis)}
                        sub="total entry value"
                      />
                    </div>
                  </div>
                </div>

                {/* Exposure */}
                <div className="stat-grid stat-grid-4">
                  <Stat label="Net Exposure" value={fmtMoney(risk?.exposures?.netExposure)}
                    sub={fmtPct(risk?.exposures?.netExposurePct, 1) + ' of Gross'}
                    cls={colorClass(risk?.exposures?.netExposure)} />
                  <Stat label="Gross Exposure" value={fmtMoney(risk?.exposures?.grossExposure)}
                    sub={fmtPct(risk?.exposures?.grossExposurePct, 0) + ' of NAV'} />
                  <Stat label="Delta-Adj Exposure" value={fmtMoney(risk?.totalDeltaAdj)}
                    sub="options delta-weighted" />
                  <Stat
                    label="Dollar Beta"
                    value={h?.dollarBeta != null ? fmtMoney(h.dollarBeta) : '—'}
                    sub={h?.beta != null ? `β = ${fmtNum(h.beta, 2)} vs SPY` : 'Run analysis'}
                  />
                </div>

                {h ? (
                  <div className="section-gap">
                    {/* Risk metrics */}
                    <div className="stat-grid stat-grid-4">
                      <Stat label="VaR 95% (1-day)" value={fmtMoney(h.var95)} sub="Historical simulation" cls="negative" />
                      <Stat label="VaR 99% (1-day)" value={fmtMoney(h.var99)} cls="negative" />
                      <Stat label="CVaR 95%" value={fmtMoney(h.cvar95)} sub="Expected shortfall" cls="negative" />
                      <Stat label="Volatility (Annual)" value={fmtNum(h.vol, 1) + '%'} sub="annualized std dev" />
                    </div>
                    <div className="stat-grid stat-grid-4">
                      <Stat label="Sharpe Ratio" value={fmtNum(h.sharpe, 2)} sub="vs 5.25% RF, annualized" cls={colorClass(h.sharpe)} />
                      <Stat label="Max Drawdown" value={fmtPct(-(h.maxDD || 0))} sub="12-month lookback" cls="negative" />
                      <Stat label="Ann. Return (12m)" value={fmtPct(h.annReturn)} cls={colorClass(h.annReturn)} />
                      <Stat label="Dollar Beta" value={fmtMoney(h.dollarBeta)} sub={`β ${fmtNum(h.beta, 2)} × ${fmtMoney(risk.totalNav)} NAV`} />
                    </div>

                    {/* Dollar Beta per position */}
                    {h.posDollarBetas?.length > 0 && (
                      <div className="card">
                        <div className="card-header">
                          <span className="card-title">Dollar Beta by Position</span>
                          <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>β × weight × NAV</span>
                        </div>
                        <div className="card-body">
                          <ResponsiveContainer width="100%" height={Math.max(120, h.posDollarBetas.length * 32)}>
                            <BarChart
                              layout="vertical"
                              data={[...h.posDollarBetas]
                                .sort((a, b) => Math.abs(b.dollar_beta) - Math.abs(a.dollar_beta))
                                .map(p => ({
                                  name: p.securities?.ticker || p.cusip || '?',
                                  value: p.dollar_beta,
                                }))}
                            >
                              <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-lo)' }}
                                tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                              <YAxis type="category" dataKey="name" width={55}
                                tick={{ fontSize: 11, fontFamily: 'var(--font-mono)', fill: 'var(--text-secondary)' }} />
                              <ReferenceLine x={0} stroke="var(--border)" />
                              <Tooltip
                                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 11 }}
                                formatter={v => [fmtMoney(v), 'Dollar Beta']}
                              />
                              <Bar dataKey="value" radius={[0, 3, 3, 0]}>
                                {h.posDollarBetas.map((p, i) => (
                                  <Cell key={i} fill={p.dollar_beta >= 0 ? '#58a6ff' : '#f85149'} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Return chart */}
                    <div className="card">
                      <div className="card-header">
                        <span className="card-title">Portfolio Return (12m, indexed to 100)</span>
                        {h.computedAt && (
                          <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>
                            Last run {new Date(h.computedAt).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div className="card-body">
                        <ResponsiveContainer width="100%" height={120}>
                          <LineChart data={(h.portPrices || []).slice(-252).map((v, i) => ({ i, v }))}>
                            <Line type="monotone" dataKey="v" stroke="#58a6ff" dot={false} strokeWidth={1.5} />
                            <ReferenceLine y={100} stroke="var(--border)" strokeDasharray="4 2" />
                            <Tooltip
                              contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 11 }}
                              formatter={v => [v.toFixed(2), 'Index']}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="card">
                    <div className="empty-state">
                      <div className="empty-state-icon">◇</div>
                      <div className="empty-state-title">Historical metrics not yet calculated</div>
                      <div className="empty-state-desc">
                        Click "Run Historical Analysis" to compute VaR, volatility, Sharpe, drawdown,
                        dollar beta, and factor loadings using 12 months of price data.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── EXPOSURE TAB ── */}
            {activeTab === 'exposure' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="card">
                  <div className="card-header"><span className="card-title">Sector Weights (% of NAV)</span></div>
                  <div className="card-body">
                    {(() => {
                      // Recompute sector directly from positions data to get accurate values
                      const sectorMap = {};
                      for (const p of filtered) {
                        const sec = p.securities?.sector || p.sector || 'Unknown';
                        const mv = Math.abs(p.market_value || 0);
                        sectorMap[sec] = (sectorMap[sec] || 0) + mv;
                      }
                      const totalGross = risk?.exposures?.grossExposure || 1;
                      const entries = Object.entries(sectorMap).sort(([, a], [, b]) => b - a);
                      if (entries.length === 0 || (entries.length === 1 && entries[0][0] === 'Unknown')) {
                        return (
                          <div style={{ color: 'var(--text-lo)', fontSize: 13 }}>
                            No sector data. Sectors are pulled from Yahoo Finance when you add a position.
                            Existing positions can be updated by re-saving them in the Portfolio page.
                          </div>
                        );
                      }
                      return entries.map(([k, v], i) => {
                        const pct = totalGross > 0 ? (v / totalGross) * 100 : 0;
                        return (
                          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1, fontFamily: 'var(--font-mono)' }}>{k}</span>
                            <div style={{ width: 80, height: 4, background: 'var(--bg-base)', borderRadius: 2 }}>
                              <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: COLORS[i % COLORS.length], borderRadius: 2 }} />
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--text-lo)', fontFamily: 'var(--font-mono)', minWidth: 60, textAlign: 'right' }}>
                              {fmtMoney(v)} / {pct.toFixed(1)}%
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                <div className="card">
                  <div className="card-header"><span className="card-title">Asset Class Weights (% of NAV)</span></div>
                  <div className="card-body">
                    {(() => {
                      const acMap = {};
                      for (const p of filtered) {
                        const ac = p.securities?.asset_class || p.asset_class || 'Other';
                        acMap[ac] = (acMap[ac] || 0) + Math.abs(p.market_value || 0);
                      }
                      const totalGross = risk?.exposures?.grossExposure || 1;
                      return Object.entries(acMap).sort(([, a], [, b]) => b - a).map(([k, v], i) => {
                        const pct = totalGross > 0 ? (v / totalGross) * 100 : 0;
                        return (
                          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[(i + 3) % COLORS.length], flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1, fontFamily: 'var(--font-mono)', textTransform: 'capitalize' }}>
                              {k.replace(/_/g, ' ')}
                            </span>
                            <div style={{ width: 80, height: 4, background: 'var(--bg-base)', borderRadius: 2 }}>
                              <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: COLORS[(i + 3) % COLORS.length], borderRadius: 2 }} />
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--text-lo)', fontFamily: 'var(--font-mono)', minWidth: 60, textAlign: 'right' }}>
                              {fmtMoney(v)} / {pct.toFixed(1)}%
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Sector detail table */}
                <div className="card" style={{ gridColumn: 'span 2' }}>
                  <div className="card-header"><span className="card-title">Positions by Sector</span></div>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Ticker</th>
                          <th>Name</th>
                          <th>Sector</th>
                          <th>Industry</th>
                          <th>Asset Class</th>
                          <th style={{ textAlign: 'right' }}>Market Value</th>
                          <th style={{ textAlign: 'right' }}>% of Gross</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...filtered].sort((a, b) => Math.abs(b.market_value || 0) - Math.abs(a.market_value || 0)).map(p => {
                          const gross = risk?.exposures?.grossExposure || 1;
                          const pct = gross > 0 ? (Math.abs(p.market_value || 0) / gross) * 100 : 0;
                          return (
                            <tr key={p.id}>
                              <td className="primary">{p.securities?.ticker || '—'}</td>
                              <td style={{ fontSize: 11, color: 'var(--text-lo)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {p.securities?.name || '—'}
                              </td>
                              <td style={{ fontSize: 11 }}>{p.securities?.sector || <span style={{ color: 'var(--text-lo)' }}>—</span>}</td>
                              <td style={{ fontSize: 11, color: 'var(--text-lo)' }}>{p.securities?.industry || '—'}</td>
                              <td style={{ fontSize: 11, textTransform: 'capitalize' }}>
                                {p.securities?.asset_class?.replace(/_/g, ' ') || '—'}
                              </td>
                              <td style={{ textAlign: 'right' }} className={colorClass(p.market_value)}>{fmtMoney(p.market_value)}</td>
                              <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{pct.toFixed(1)}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── GREEKS TAB ── */}
            {activeTab === 'greeks' && (
              <div className="card">
                <div className="card-header">
                  <span className="card-title">Portfolio Greeks</span>
                  <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>Equity positions: delta = ±1, others blank</span>
                </div>
                <div className="card-body">
                  <div className="stat-grid stat-grid-4" style={{ marginBottom: 20 }}>
                    <Stat label="Net Delta-Adj ($)" value={fmtMoney(risk?.totalDeltaAdj)} sub="delta-weighted exposure" />
                    <Stat
                      label="Net Delta Σ"
                      value={fmtNum(filtered.reduce((s, p) =>
                        s + (p.delta != null ? p.delta * p.quantity : (p.direction === 'long' ? p.quantity : -p.quantity)), 0), 2)}
                      sub="sum of all deltas"
                    />
                    <Stat
                      label="Net Gamma Σ"
                      value={fmtNum(filtered.filter(p => p.gamma != null).reduce((s, p) => s + p.gamma * p.quantity, 0), 4)}
                    />
                    <Stat
                      label="Net Theta Σ"
                      value={fmtNum(filtered.filter(p => p.theta != null).reduce((s, p) => s + p.theta * p.quantity, 0), 2)}
                      sub="daily $ decay"
                    />
                    <Stat
                      label="Net Vega Σ"
                      value={fmtNum(filtered.filter(p => p.vega != null).reduce((s, p) => s + p.vega * p.quantity, 0), 2)}
                      sub="per 1% IV move"
                    />
                  </div>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Ticker</th><th>Direction</th><th>Qty</th>
                          <th>δ Delta</th><th>Δ-adj Exp</th>
                          <th>γ Gamma</th><th>θ Theta</th><th>ν Vega</th><th>ρ Rho</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(p => (
                          <tr key={p.id}>
                            <td className="primary">{p.securities?.ticker || p.cusip}</td>
                            <td><span className={`badge badge-${p.direction}`}>{p.direction}</span></td>
                            <td>{fmtNum(p.quantity, 0)}</td>
                            <td className={colorClass(p.delta)}>{fmtNum(p.delta, 4)}</td>
                            <td>{fmtMoney(p.delta_adjusted_exposure)}</td>
                            <td>{fmtNum(p.gamma, 5)}</td>
                            <td className="negative">{fmtNum(p.theta, 4)}</td>
                            <td>{fmtNum(p.vega, 3)}</td>
                            <td>{fmtNum(p.rho, 4)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── FACTORS TAB ── */}
            {activeTab === 'factors' && (
              <div className="section-gap">
                {h?.ff ? (
                  <>
                    <div className={`alert ${h.ff.source === 'estimated' ? 'alert-warning' : 'alert-info'}`}>
                      {h.ff.source === 'ken_french'
                        ? `✓ Using real Ken French daily factor data (${h.ff.observations} observations). Fama-French 6-factor model.`
                        : '⚠ Factor data unavailable from Ken French — using historical average estimates. Results are approximate.'}
                    </div>

                    <div className="stat-grid stat-grid-4">
                      <Stat label="Market Beta (FF)" value={fmtNum(h.ff.market_beta, 3)}
                        sub="Fama-French market factor" />
                      <Stat label="Dollar Beta (FF)" value={fmtMoney(h.ff.market_beta * risk.totalNav)}
                        sub="β × NAV" />
                      <Stat label="MOM Loading" value={fmtNum(h.ff.mom, 3)}
                        sub="momentum factor" cls={colorClass(h.ff.mom)} />
                      <Stat label="R² (to FF6)" value={fmtNum(h.ff.r_squared * 100, 1) + '%'}
                        sub={`${h.ff.observations} daily obs`} />
                    </div>



                    <div className="card">
                      <div className="card-header">
                        <span className="card-title">Factor Loadings</span>
                        <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>FF6 OLS regression coefficients</span>
                      </div>
                      <div className="card-body">
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={[
                            { name: 'Market β', value: h.ff.market_beta },
                            { name: 'MOM', value: h.ff.mom },
                          ]}>
                            <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: 'var(--font-mono)', fill: 'var(--text-lo)' }} />
                            <YAxis tick={{ fontSize: 10, fill: 'var(--text-lo)' }} />
                            <ReferenceLine y={0} stroke="var(--border)" />
                            <Tooltip
                              contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 11 }}
                              formatter={v => [v.toFixed(4), 'Loading']}
                            />
                            <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                              {[0, 1].map(i => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="card"><div className="empty-state">
                    <div className="empty-state-icon">◇</div>
                    <div className="empty-state-title">Run Historical Analysis first</div>
                    <div className="empty-state-desc">
                      Factor loadings (Fama-French 6-factor: Market, SMB, HML, RMW, CMA, MOM) will appear here.
                      Uses real daily factor data from Ken French's data library when available.
                    </div>
                  </div></div>
                )}
              </div>
            )}

            {/* ── CORRELATION TAB ── */}
            {activeTab === 'correlation' && (
              <div className="card">
                <div className="card-header">
                  <span className="card-title">Return Correlation Matrix</span>
                  <span style={{ fontSize: 11, color: 'var(--text-lo)' }}>Pearson, trailing 12m daily returns</span>
                </div>
                <div className="card-body">
                  {h?.correlation ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '6px 10px', fontSize: 10, color: 'var(--text-lo)', fontFamily: 'var(--font-mono)' }}></th>
                            {Object.keys(h.correlation).map(t => (
                              <th key={t} style={{ padding: '6px 10px', fontSize: 10, color: 'var(--text-lo)', fontFamily: 'var(--font-mono)', textAlign: 'center' }}>{t}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(h.correlation).map(([t1, row]) => (
                            <tr key={t1}>
                              <td style={{ padding: '6px 10px', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', fontWeight: 500 }}>{t1}</td>
                              {Object.entries(row).map(([t2, v]) => {
                                const col = v == null ? '' : v > 0.7 ? 'var(--red)' : v > 0.4 ? 'var(--yellow)' : v < -0.3 ? 'var(--green)' : 'var(--text-lo)';
                                return (
                                  <td key={t2} style={{
                                    padding: '6px 10px', textAlign: 'center', fontSize: 11,
                                    fontFamily: 'var(--font-mono)', color: col,
                                    background: t1 === t2 ? 'rgba(88,166,255,0.1)' : ''
                                  }}>
                                    {v == null ? '—' : v.toFixed(2)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state"><div className="empty-state-desc">Run Historical Analysis to compute pairwise correlations.</div></div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ApiConfirmModal
        isOpen={showConfirm}
        onConfirm={handleCalcHistorical}
        onCancel={() => setShowConfirm(false)}
        action={`Fetch 12 months of historical prices for ${[...new Set(filtered.map(p => p.securities?.ticker).filter(Boolean))].length} securities + SPY + Fama-French factors`}
        provider="yahoo_finance"
        estimatedCalls={[...new Set(filtered.map(p => p.securities?.ticker).filter(Boolean))].length + 1}
      />
    </div>
  );
}
